import React, { useEffect, useState } from 'react';
import { useMountedState } from 'react-use';

import ErrorBoundary from 'components/ErrorBoundary';
import MapWindStationPin from 'components/MapWindStationPin';
import { Pane } from 'components/Map';
import useMap from 'hooks/map/useMap';
import type { Speed } from 'types/units';
import type { NearbyStation } from 'types/windStations';

import styles from './MapWindStationPins.module.scss';

interface Props {
  windStations?: NearbyStation[];
  windSpeedUnits: Speed;
  hasLiveWindPermissions: boolean;
}

const MapWindStationPins: React.FC<Props> = ({
  windStations = [],
  windSpeedUnits,
  hasLiveWindPermissions,
}) => {
  const [map] = useMap();
  const isMounted = useMountedState();
  const [mapReady, setMapReady] = useState(false);

  // Watch for map changes and ensure we re-render when a new map instance is available
  useEffect(() => {
    if (map?.getContainer?.()) {
      // Add a small delay to ensure the map container is fully ready
      const timer = setTimeout(() => {
        if (isMounted()) {
          setMapReady(true);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
    setMapReady(false);
    return undefined;
  }, [map, isMounted]);

  // If component is unmounted or map is not ready, don't render to prevent state updates
  if (!isMounted() || !map || !mapReady) {
    return null;
  }

  return (
    <Pane
      name="map-wind-station-pins"
      className={styles.pins}
      usingPortal
      map={map}
      // This is a required prop in quiver, removing it throws an error
      style={{}}
    >
      {windStations.map((windStation) => (
        // Don't render anything if the marker throws
        <ErrorBoundary
          errorBoundaryId="map-wind-station-pins"
          key={windStation._id}
          render={() => null}
        >
          <MapWindStationPin
            data={windStation}
            units={windSpeedUnits}
            hasLiveWindPermissions={hasLiveWindPermissions}
          />
        </ErrorBoundary>
      ))}
    </Pane>
  );
};

export default MapWindStationPins;
